// Subscription hook — wraps `@barel/api-client`'s `useBilling` and adds
// a small selector helper for the trial/expired UI states. Subscribes to
// the WS `subscription.updated` event by invalidating the react-query
// cache key whenever it fires.

import { useEffect } from 'react';

import { type BillingResponse, billingSubscriptionKey, useBilling } from '@barel/api-client/react';
import { useQueryClient } from '@tanstack/react-query';

export type { BillingResponse } from '@barel/api-client/react';

interface UseSubscriptionOptions {
  /** Override the polling interval (default: 60s while focused). */
  refetchInterval?: number;
  /** Disable the hook in tests / story fixtures. */
  enabled?: boolean;
}

export function useSubscription(options: UseSubscriptionOptions = {}) {
  const qc = useQueryClient();
  const query = useBilling({
    enabled: options.enabled,
    refetchInterval: options.refetchInterval ?? 60_000,
  });

  // WS subscription — ws-attach to invalidate. Wave 3.1 owns the WS shell;
  // we listen on a `BroadcastChannel` shim that the shell forwards to.
  useEffect(() => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;
    const ch = new BroadcastChannel('barel:realtime');
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'subscription.updated') {
        void qc.invalidateQueries({ queryKey: billingSubscriptionKey });
      }
    };
    ch.addEventListener('message', onMessage);
    return () => {
      ch.removeEventListener('message', onMessage);
      ch.close();
    };
  }, [qc]);

  return query;
}

/** Convenience selector — returns the trial envelope or a sane default. */
export function getTrialEnvelope(data: BillingResponse | undefined) {
  return (
    data?.trial ?? {
      status: 'trialing' as const,
      plan: 'discover' as const,
      trialEndsAt: null,
      trialDaysLeft: null,
      bannerState: null,
      accessTier: 'full' as const,
      expired: false,
    }
  );
}
